import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { TranslateService } from '@ngx-translate/core';

import { UtilsService } from './utils.service';

export interface CEP {
  cep: string;
  uf: string;
  localidade: string;
  logradouro: string;
  bairro: string;
  complemento: string;
}

@Injectable({
  providedIn: 'root',
})
export class ZipcodeSearchService {
  constructor(
    private utilsService: UtilsService,
    private translate: TranslateService,
    private httpClient: HttpClient
  ) {}

  async searchStateAndCityByZipCode(zipCode: string, timeoutInMs: number = 12000): Promise<CEP | any> {
    const zipCodeOnlyNumbers = zipCode ? zipCode.replace(/[^\d]+/g, '') : null;
    if (zipCodeOnlyNumbers && zipCodeOnlyNumbers.length >= 8) {
      try {
        const responseViaCep = await Promise.race([
          this.utilsService.timeoutToReject(
            `Zipcode Service timeout reached (limit: ${timeoutInMs} ms)`,
            timeoutInMs
          ),
          this.httpClient.get<CEP>(`https://viacep.com.br/ws/${zipCodeOnlyNumbers}/json`).toPromise(),
        ]);

        if (!responseViaCep || responseViaCep['erro']) {
          // If there's an error or no response from ViaCep, try the second API
          const responseBrasilApi = await Promise.race([
            this.utilsService.timeoutToReject(
              `BrasilApi Service timeout reached (limit: ${timeoutInMs} ms)`,
              timeoutInMs
            ),
            this.httpClient.get<CEP>(`https://brasilapi.com.br/api/cep/v1/${zipCodeOnlyNumbers}`).toPromise(),
          ]);

          if (!responseBrasilApi || responseBrasilApi['erro']) {
            // If there's an error or no response from both APIs, show an error message
            this.utilsService.showToast('', `${this.translate.instant('PROVIDERS.WE_CANT_GET')}`);
            return {};
          }
          const typedResponseBrasilApi = responseBrasilApi as CEP;

          const updatedResponse = {
            ...typedResponseBrasilApi,
            localidade: typedResponseBrasilApi['city'],
            logradouro: typedResponseBrasilApi['street'],
            uf: typedResponseBrasilApi['state'],
            bairro: typedResponseBrasilApi['neighborhood'],
          };

          return updatedResponse;
        }

        return responseViaCep;
      } catch (error) {
        console.error(error);
        this.utilsService.showToast('', `${this.translate.instant('PROVIDERS.WE_CANT_GET')}`);
        return {};
      }
    }
    return {};
  }

  async searchCNPJ(cnpj: string) {
    const cnpjNum = cnpj.replace(/[^0-9]/g, '');

    try {
      const response = await this.httpClient.get(`${environment.apiUrl}/v1/cnpj?cnpj=` + cnpjNum).toPromise();
      return response;
    } catch (error) {
      console.error(error);
      this.utilsService.showToast(
        '',
        error?.error ?? `${this.translate.instant('COMPONENTS.FORMS.ERROR_WHEN_FIND_CNPJ')}`
      );
      return {};
    }
  }
}
